import { SITE } from '@maktech/shared';

/**
 * seo.ts — the single module every meta value flows through (spec 0.2:
 * "Keep every meta value in one seo.ts module so a later Next.js migration is
 * a swap, not a rewrite").
 *
 * Mirrors setMeta() (docs/index.html:4563) exactly:
 *   title      `${title} — ${SITE.name}`, or the site default when absent
 *   description desc || SITE.desc
 *   canonical  SITE.base + path
 *   og:        title, description, url, type (default "website"), site_name,
 *              image (only when given)
 *   twitter:   card=summary_large_image, title, description
 *   ld+json    the supplied object, or an Organization fallback
 *
 * NO react-helmet-async. Part 2 names it alongside React 19, but it
 * peer-depends on React ^16/^17/^18 and was last released before React 19
 * shipped — the two cannot both be built. React 19 hoists <title>/<meta>/
 * <link> rendered anywhere in the tree into <head> natively, including under
 * prerender, so the dependency is not merely incompatible but unnecessary.
 */

export interface MetaInput {
  title?: string;
  desc?: string;
  path?: string;
  image?: string;
  type?: string;
  jsonld?: Record<string, unknown>;
}

export interface ResolvedMeta {
  title: string;
  description: string;
  canonical: string;
  ogType: string;
  ogImage: string | undefined;
  siteName: string;
  jsonld: Record<string, unknown>;
}

/** The default LD when a page supplies none — setMeta (:4588). */
export function organizationLd(): Record<string, unknown> {
  return { '@context': 'https://schema.org', '@type': 'Organization', name: SITE.name, url: SITE.base };
}

export function resolveMeta(m: MetaInput): ResolvedMeta {
  return {
    // :4564 — `title?title+" — "+SITE.name:SITE.name+" — Learn skills. Earn certificates."`
    title: m.title ? `${m.title} — ${SITE.name}` : `${SITE.name} — Learn skills. Earn certificates.`,
    description: m.desc ?? SITE.desc,
    canonical: `${SITE.base}${m.path ?? '/'}`,
    ogType: m.type ?? 'website',
    ogImage: m.image,
    siteName: SITE.name,
    jsonld: m.jsonld ?? organizationLd(),
  };
}

// ---------------------------------------------------------------------------
// JSON-LD builders — spec 0.2 names Course, Article, Person, Event, Blog.
// ---------------------------------------------------------------------------

/** Course JSON-LD — v12 openCourse (:4789). */
export function courseLd(c: {
  title: string;
  description: string;
  rating: number;
  ratingCount: number;
  price: number;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: c.title,
    description: c.description,
    provider: { '@type': 'Organization', name: SITE.name, sameAs: SITE.base },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: c.rating, ratingCount: c.ratingCount },
    offers: {
      '@type': 'Offer',
      // The prototype emitted priceCurrency:"USD" (:4792). Currency is BDT per
      // the client's Part 12 decision, and price is poisha, so it is converted
      // back to taka here — schema.org expects a decimal amount, not minor
      // units. Shipping USD would misprice every course in Google's eyes.
      price: (c.price / 100).toFixed(2),
      priceCurrency: 'BDT',
    },
  };
}

export function articleLd(a: {
  title: string;
  excerpt: string;
  author: string;
  publishedAt: string;
  slug: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    description: a.excerpt,
    author: { '@type': 'Person', name: a.author },
    publisher: { '@type': 'Organization', name: SITE.name, url: SITE.base },
    datePublished: a.publishedAt,
    mainEntityOfPage: `${SITE.base}/guides/${a.slug}`,
  };
}

export function personLd(p: { name: string; role: string; bio: string | null; slug: string }): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: p.name,
    jobTitle: p.role,
    ...(p.bio ? { description: p.bio } : {}),
    url: `${SITE.base}/instructors/${p.slug}`,
    worksFor: { '@type': 'Organization', name: SITE.name, url: SITE.base },
  };
}

export function eventLd(e: {
  title: string;
  startsAt: string;
  description: string;
  slug: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: e.title,
    startDate: e.startsAt,
    description: e.description,
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: { '@type': 'VirtualLocation', url: `${SITE.base}/event/${e.slug}` },
    organizer: { '@type': 'Organization', name: SITE.name, url: SITE.base },
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'BDT', availability: 'https://schema.org/InStock' },
  };
}

export function blogLd(articles: { title: string; slug: string; excerpt: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: `${SITE.name} — Free notes & guides`,
    url: `${SITE.base}/guides`,
    blogPost: articles.map((a) => ({
      '@type': 'BlogPosting',
      headline: a.title,
      description: a.excerpt,
      url: `${SITE.base}/guides/${a.slug}`,
    })),
  };
}
